'use client';

import React from 'react';
import Link from 'next/link';
import { Zap, Database, Users, Mail, Shield, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIntersectionObserver } from '@/app/hooks/useIntersectionObserver';

const WhatWeDo: React.FC = () => {
  const { t } = useTranslation();
  const { ref, isVisible } = useIntersectionObserver();

  const features = [
    {
      icon: <Zap className="w-6 h-6 text-primary-500" />,
      title: t('pageContent.home.whatWeDo.features.distribution.title'),
      description: t('pageContent.home.whatWeDo.features.distribution.description'),
    },
    {
      icon: <Database className="w-6 h-6 text-primary-500" />,
      title: t('pageContent.home.whatWeDo.features.matching.title'),
      description: t('pageContent.home.whatWeDo.features.matching.description'),
    },
    {
      icon: <Users className="w-6 h-6 text-primary-500" />,
      title: t('pageContent.home.whatWeDo.features.investors.title'),
      description: t('pageContent.home.whatWeDo.features.investors.description'),
    },
    {
      icon: <Mail className="w-6 h-6 text-primary-500" />,
      title: t('pageContent.home.whatWeDo.features.contact.title'),
      description: t('pageContent.home.whatWeDo.features.contact.description'),
    },
    {
      icon: <Shield className="w-6 h-6 text-primary-500" />,
      title: t('pageContent.home.whatWeDo.features.security.title'),
      description: t('pageContent.home.whatWeDo.features.security.description'),
    },
    {
      icon: <Check className="w-6 h-6 text-primary-500" />,
      title: t('pageContent.home.whatWeDo.features.pricing.title'),
      description: t('pageContent.home.whatWeDo.features.pricing.description'),
    },
  ];

  return (
    <section id="what-we-do" className="py-16 bg-gradient-to-b from-white to-gray-50">
      <div className="container">
        <div ref={ref} className={`text-center mb-12 fade-in-section ${isVisible ? 'is-visible' : ''}`}>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('pageContent.home.whatWeDo.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('pageContent.home.whatWeDo.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`bg-white p-6 md:p-8 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 hover:translate-y-[-2px] group fade-in-section ${isVisible ? 'is-visible' : ''}`}
              style={{ transitionDelay: `${index * 0.1}s` }}
            >
              <div className="rounded-xl bg-primary-50 w-12 h-12 flex items-center justify-center mb-6 group-hover:bg-primary-100 transition-colors">
                <div className="hover-rotate">
                  {feature.icon}
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
              <p className="text-gray-600 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Contextual links to the three money pages.
            Before this, /free-vc-list, /pricing and /pitch-deck-templates were
            reachable from the homepage ONLY through the header dropdown and the
            footer — i.e. site chrome, which every page carries and which passes
            comparatively little signal. An SEO audit flagged zero editorial
            inlinks from the homepage to any of them. These three are real
            next-steps for a visitor who is not ready to upload a deck yet, so
            they earn their place for users as well as for crawlers. */}
        <div className={`mt-14 fade-in-section ${isVisible ? 'is-visible' : ''}`}>
          <h3 className="text-center text-lg font-semibold text-gray-900 mb-6">
            {t('pageContent.home.whatWeDo.resources.title')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { href: '/free-vc-list', key: 'freeList' },
              { href: '/pitch-deck-templates', key: 'templates' },
              { href: '/pricing', key: 'pricing' },
            ].map(({ href, key }) => (
              <Link
                key={href}
                href={href}
                className="block bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-primary-200 transition-all duration-300"
              >
                <span className="block font-semibold text-primary-700 mb-1">
                  {t(`pageContent.home.whatWeDo.resources.${key}.title`)}
                </span>
                <span className="block text-sm text-gray-600">
                  {t(`pageContent.home.whatWeDo.resources.${key}.description`)}
                </span>
              </Link>
            ))}
          </div>
          <p className="mt-6 text-center text-sm text-gray-600">
            {t('pageContent.home.whatWeDo.resources.guides.prefix')}{' '}
            <Link href="/how-it-works" className="text-primary-700 underline hover:text-primary-800">
              {t('pageContent.home.whatWeDo.resources.guides.howItWorks')}
            </Link>
            ,{' '}
            <Link href="/blog/find-investors-for-seed-round" className="text-primary-700 underline hover:text-primary-800">
              {t('pageContent.home.whatWeDo.resources.guides.findInvestors')}
            </Link>
            , or{' '}
            <Link href="/blog/investor-outreach-automation-tools-compared" className="text-primary-700 underline hover:text-primary-800">
              {t('pageContent.home.whatWeDo.resources.guides.compare')}
            </Link>
            .
          </p>
        </div>
      </div>
    </section>
  );
};

export { WhatWeDo };
